(function () {
  const manifestSelector = "[data-bb-product-group-reviews-manifest]";
  const endpointBase = "https://api-cdn.yotpo.com/v1/widget";
  const cachePrefix = "bb:yotpo-product-reviews:v1:";
  const cacheTtl = 1000 * 60 * 60 * 6;
  const perPage = 5;
  const maxConcurrent = 3;
  const minBodyLength = 24;
  const carouselLimit = 6;
  const mobileInitialCountMedia = window.matchMedia("(max-width: 749px)");

  const state = {
    status: "idle",
    collectionHandle: "",
    productIds: [],
    productReviews: {},
    featuredByProductId: {},
    carouselCandidates: [],
    allCandidates: [],
    productPages: {},
    hasMoreByProductId: {},
    requestCount: 0,
    errors: []
  };

  let manifestElement = null;
  let loadToken = 0;

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const reviewYear = (review) => {
    const timestamp = Date.parse(review.createdAt || "");
    if (!timestamp) return "";
    return String(new Date(timestamp).getFullYear());
  };

  const parseManifest = () => {
    const element = document.querySelector(manifestSelector);
    manifestElement = element;
    if (!element) return null;

    try {
      const manifest = JSON.parse(element.textContent || "{}");
      if (!manifest.yotpoAppKey || !Array.isArray(manifest.products)) return null;
      return {
        collectionHandle: String(manifest.collectionHandle || ""),
        yotpoAppKey: String(manifest.yotpoAppKey),
        products: manifest.products
          .map((product) => ({
            id: String(product.id || ""),
            handle: String(product.handle || ""),
            url: String(product.url || ""),
            title: String(product.title || ""),
            imageUrl: String(product.imageUrl || ""),
            imageAlt: String(product.imageAlt || product.title || "")
          }))
          .filter((product) => product.id)
      };
    } catch (error) {
      state.errors.push({ scope: "manifest", message: error.message });
      return null;
    }
  };

  const cacheKey = (appKey, productId, page) => `${cachePrefix}${appKey}:${productId}:p${perPage}:${page}`;

  const readCache = (key) => {
    try {
      const cached = window.sessionStorage.getItem(key);
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      if (!parsed || Date.now() - parsed.createdAt > cacheTtl) {
        window.sessionStorage.removeItem(key);
        return null;
      }
      return parsed.value || null;
    } catch (error) {
      return null;
    }
  };

  const writeCache = (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ createdAt: Date.now(), value }));
    } catch (error) {
      // Storage can be unavailable in privacy modes. The page should remain fully usable.
    }
  };

  const normalizeReview = (review, product) => {
    const user = review.user || {};
    const body = String(review.content || "").trim();
    const title = String(review.title || "").trim();
    const rating = Number(review.score || 0);
    const reviewId = review.id != null ? String(review.id) : "";

    if (!reviewId || !rating || !body) return null;

    return {
      productId: product.id,
      productHandle: product.handle,
      productUrl: product.url,
      productTitle: product.title,
      productImageUrl: product.imageUrl,
      productImageAlt: product.imageAlt,
      yotpoProductId: review.product_id != null ? String(review.product_id) : "",
      reviewId,
      rating,
      title,
      body,
      reviewerName: String(user.display_name || "").trim(),
      verified: Boolean(review.verified_buyer),
      published: review.deleted !== true,
      createdAt: String(review.created_at || ""),
      media: Array.isArray(review.images_data) ? review.images_data : []
    };
  };

  const isSuitableReview = (review) =>
    review &&
    review.published &&
    review.rating === 5 &&
    review.body.length >= minBodyLength;

  const reviewRank = (review) => [
    review.published ? 1 : 0,
    review.verified ? 1 : 0,
    review.rating === 5 ? 1 : 0,
    review.body.length >= minBodyLength ? 1 : 0,
    Date.parse(review.createdAt) || 0
  ];

  const compareReviews = (a, b) => {
    const rankA = reviewRank(a);
    const rankB = reviewRank(b);

    for (let index = 0; index < rankA.length; index += 1) {
      if (rankA[index] !== rankB[index]) return rankB[index] - rankA[index];
    }

    return String(a.reviewId).localeCompare(String(b.reviewId));
  };

  const fetchProductReviews = async (manifest, product, page = 1) => {
    const key = cacheKey(manifest.yotpoAppKey, product.id, page);
    const cached = readCache(key);
    if (cached) return { product, payload: cached, cached: true, page };

    const url = `${endpointBase}/${encodeURIComponent(manifest.yotpoAppKey)}/products/${encodeURIComponent(
      product.id
    )}/reviews.json?per_page=${perPage}&page=${page}&sort=date&direction=desc`;

    state.requestCount += 1;
    const response = await fetch(url, { credentials: "omit" });
    if (!response.ok) {
      throw new Error(`Yotpo ${response.status} for ${product.id}`);
    }

    const payload = await response.json();
    writeCache(key, payload);
    return { product, payload, cached: false, page };
  };

  const mapProductPayload = ({ product, payload, page = 1 }) => {
    const reviews = (((payload || {}).response || {}).reviews || [])
      .map((review) => normalizeReview(review, product))
      .filter(Boolean)
      .sort(compareReviews);

    const pagination = ((payload || {}).response || {}).pagination || {};
    const totalPages = Number(pagination.total_pages || pagination.pages || 0);

    return {
      productId: product.id,
      productHandle: product.handle,
      page,
      reviewsReturned: reviews.length,
      reviews,
      selectedReview: reviews.find(isSuitableReview) || null,
      hasMore: totalPages ? page < totalPages : reviews.length === perPage
    };
  };

  const runWithConcurrency = async (items, worker, limit, errors) => {
    const results = [];
    let cursor = 0;

    const runNext = async () => {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;

      try {
        results[index] = await worker(items[index]);
      } catch (error) {
        errors.push({ productId: items[index].id, message: error.message });
        results[index] = null;
      }

      await runNext();
    };

    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runNext));
    return results.filter(Boolean);
  };

  const selectCarouselCandidates = (productResults) => {
    const selected = [];
    const productQueues = productResults
      .map((result) => ({
        productId: result.productId,
        reviews: result.reviews.filter(isSuitableReview)
      }))
      .filter((entry) => entry.reviews.length);

    let round = 0;
    while (selected.length < carouselLimit && productQueues.some((entry) => entry.reviews[round])) {
      for (const entry of productQueues) {
        const review = entry.reviews[round];
        if (review && selected.length < carouselLimit) selected.push(review);
      }
      round += 1;
    }

    return selected;
  };

  const selectAllCandidates = (productResults) => {
    const selected = [];
    const seen = new Set();
    const productQueues = productResults
      .map((result) => ({ reviews: result.reviews.filter(isSuitableReview) }))
      .filter((entry) => entry.reviews.length);
    const longestQueue = Math.max(0, ...productQueues.map((entry) => entry.reviews.length));

    for (let round = 0; round < longestQueue; round += 1) {
      productQueues.forEach((entry) => {
        const review = entry.reviews[round];
        if (review && !seen.has(review.reviewId)) {
          seen.add(review.reviewId);
          selected.push(review);
        }
      });
    }

    return selected;
  };

  const publishState = (nextState) => {
    Object.assign(state, nextState);
    window.BitebackProductGroupReviews = state;
    renderFeaturedReviews();
    renderTestimonials();
    renderProgressiveReviews();
    if (manifestElement) {
      manifestElement.dataset.bbReviewPoolStatus = state.status;
      manifestElement.dataset.bbReviewPoolRequestCount = String(state.requestCount);
      manifestElement.dataset.bbReviewPoolProductCount = String(state.productIds.length);
      manifestElement.dataset.bbReviewPoolFeaturedCount = String(Object.keys(state.featuredByProductId).length);
      manifestElement.dataset.bbReviewPoolCarouselCount = String(state.carouselCandidates.length);
      manifestElement.dataset.bbReviewPoolCandidateCount = String(state.allCandidates.length);
      manifestElement.dataset.bbReviewPoolErrorCount = String(state.errors.length);
      manifestElement.dataset.bbReviewPoolRenderedCardCount = String(
        document.querySelectorAll("[data-bb-product-card-review][data-review-id]").length
      );
      manifestElement.dataset.bbReviewPoolRenderedTestimonialCount = String(
        document.querySelectorAll("[data-bb-product-group-testimonial-card][data-review-id]").length
      );
    }
    document.dispatchEvent(
      new CustomEvent("biteback:product-group-reviews:ready", {
        detail: state
      })
    );
  };

  const renderFeaturedReviews = () => {
    document.querySelectorAll("[data-bb-product-card-review][data-product-id]").forEach((target) => {
      const productId = String(target.dataset.productId || "");
      const review = state.featuredByProductId[productId];

      if (!review) {
        target.innerHTML = "";
        target.hidden = true;
        target.removeAttribute("data-review-id");
        return;
      }

      const reviewer = review.reviewerName ? `${escapeHtml(review.reviewerName)}, verified buyer` : "Verified buyer";
      target.innerHTML = `
        <p>&ldquo;${escapeHtml(review.body)}&rdquo;</p>
        <span>&mdash; ${reviewer}</span>
      `;
      target.dataset.reviewId = review.reviewId;
      target.dataset.reviewProductId = review.productId;
      target.hidden = false;
    });
  };

  const starMarkup = (rating) => {
    const score = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return Array.from({ length: 5 }, (_, index) => {
      const isFilled = index < score;
      return `
        <span${isFilled ? "" : ' class="is-muted"'}>
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
          </svg>
        </span>
      `;
    }).join("");
  };

  const renderTestimonials = () => {
    const section = document.querySelector("[data-bb-product-group-testimonials]");
    if (!section || !state.carouselCandidates.length) return;

    const cards = Array.from(section.querySelectorAll("[data-bb-product-group-testimonial-card]"));
    state.carouselCandidates.slice(0, cards.length).forEach((review, index) => {
      const card = cards[index];
      const stars = card.querySelector("[data-bb-testimonial-stars]");
      const title = card.querySelector("[data-bb-testimonial-title]");
      const body = card.querySelector("[data-bb-testimonial-body]");
      const byline = card.querySelector("[data-bb-testimonial-byline]");
      const year = card.querySelector("[data-bb-testimonial-year]");

      if (stars) {
        stars.innerHTML = starMarkup(review.rating);
        stars.setAttribute("aria-label", `${review.rating} out of 5 stars`);
      }

      if (title) {
        if (review.title) {
          title.textContent = review.title;
          title.hidden = false;
        } else {
          title.textContent = "";
          title.hidden = true;
        }
      }

      if (body) body.textContent = `"${review.body}"`;
      if (byline) {
        const reviewer = review.reviewerName || "Verified buyer";
        byline.firstChild ? (byline.firstChild.textContent = `${reviewer} `) : byline.prepend(`${reviewer} `);
      }
      if (year) year.textContent = reviewYear(review);

      card.dataset.reviewId = review.reviewId;
      card.dataset.reviewProductId = review.productId;
      card.dataset.reviewProductHandle = review.productHandle;
      card.dataset.reviewRating = String(review.rating);
    });
  };

  const productMarkup = (review, className) => {
    const productMedia = review.productImageUrl
      ? `<img src="${escapeHtml(review.productImageUrl)}" alt="${escapeHtml(review.productImageAlt)}" width="96" height="96" loading="lazy">`
      : "";
    return review.productUrl && review.productTitle
      ? `<a class="bb-review-product ${className}" href="${escapeHtml(review.productUrl)}">
          <span class="bb-review-product__media">${productMedia}</span>
          <span>${escapeHtml(review.productTitle)}</span>
        </a>`
      : "";
  };

  const homeCardMarkup = (review) => {
    const product = productMarkup(review, "bb-home-review-card__product");
    const reviewer = review.reviewerName || "Verified buyer";
    const verified = review.verified ? " · Verified buyer" : "";

    return `<article class="bb-home-review-card" role="listitem" data-review-id="${escapeHtml(review.reviewId)}" data-review-product-id="${escapeHtml(review.productId)}">
      ${product}
      <div class="bb-home-stars" aria-label="${review.rating} out of 5 stars">${starMarkup(review.rating)}</div>
      ${review.title ? `<h3>${escapeHtml(review.title)}</h3>` : ""}
      <blockquote>&ldquo;${escapeHtml(review.body)}&rdquo;</blockquote>
      <p>${escapeHtml(reviewer)}${verified}</p>
    </article>`;
  };

  const testimonialCardMarkup = (review) => {
    const product = productMarkup(review, "bb-testimonial-card__product");
    const reviewer = review.reviewerName || "Verified buyer";
    const verified = review.verified ? " · Verified buyer" : "";
    const year = reviewYear(review);

    return `<article class="bb-testimonial-card" role="listitem" data-bb-product-group-testimonial-card data-review-id="${escapeHtml(review.reviewId)}" data-review-product-id="${escapeHtml(review.productId)}">
      ${product}
      <div class="bb-home-stars" aria-label="${review.rating} out of 5 stars">${starMarkup(review.rating)}</div>
      ${review.title ? `<h3>${escapeHtml(review.title)}</h3>` : ""}
      <blockquote class="bb-testimonial-card__body">&ldquo;${escapeHtml(review.body)}&rdquo;</blockquote>
      <p class="bb-testimonial-card__byline">${escapeHtml(reviewer)}${verified}${year ? ` <span>${escapeHtml(year)}</span>` : ""}</p>
    </article>`;
  };

  const progressiveReviewState = new WeakMap();

  const initialReviewCount = (section) => {
    const desktopCount = Number(section.dataset.initialCount || 3);
    if (!section.matches("[data-bb-home-reviews]") || !mobileInitialCountMedia.matches) return desktopCount;
    return Number(section.dataset.mobileInitialCount || desktopCount);
  };

  const renderProgressiveReviews = () => {
    document.querySelectorAll("[data-bb-progressive-reviews], [data-bb-home-reviews]").forEach((section) => {
      const list = section.querySelector("[data-bb-review-list], [data-bb-home-review-list]");
      const actions = section.querySelector("[data-bb-review-actions], [data-bb-home-review-actions]");
      const more = section.querySelector("[data-bb-review-more], [data-bb-home-review-more]");
      const all = section.querySelector("[data-bb-review-all], [data-bb-home-review-all]");
      if (!list || !actions || !more || !all) return;

      const initialCount = initialReviewCount(section);
      const local = progressiveReviewState.get(section) || {
        visibleCount: initialCount,
        initialCount,
        loading: false,
        expanded: false
      };
      if (!local.expanded && local.initialCount !== initialCount) {
        local.initialCount = initialCount;
        local.visibleCount = initialCount;
      }
      progressiveReviewState.set(section, local);

      const visible = state.allCandidates.slice(0, local.visibleCount);
      const cardMarkup = section.dataset.bbReviewVariant === "testimonial" ? testimonialCardMarkup : homeCardMarkup;
      list.innerHTML = visible.map(cardMarkup).join("");
      section.dataset.bbReviewCount = String(visible.length);
      if (section.matches("[data-bb-home-reviews]")) section.dataset.bbHomeReviewCount = String(visible.length);
      actions.hidden = state.status === "loading" || visible.length === 0;
      const cachedMoreAvailable = local.visibleCount < state.allCandidates.length;
      const remoteMoreAvailable = Object.values(state.hasMoreByProductId).some(Boolean);
      const enableViewMore = section.dataset.enableViewMore !== "false";
      const showFinalCta = section.dataset.showFinalCta !== "false";
      more.hidden = !enableViewMore || (!cachedMoreAvailable && !remoteMoreAvailable);
      more.disabled = local.loading;
      more.textContent = local.loading ? "Loading reviews…" : "View more reviews";
      all.hidden = !showFinalCta || (enableViewMore && (cachedMoreAvailable || remoteMoreAvailable));
    });
  };

  const loadMoreReviews = async (section) => {
    const local = progressiveReviewState.get(section);
    if (!local || local.loading) return;
    const pageSize = Number(section.dataset.pageSize || 5);
    local.expanded = true;

    if (local.visibleCount < state.allCandidates.length) {
      local.visibleCount += pageSize;
      renderProgressiveReviews();
      return;
    }

    const manifest = parseManifest();
    if (!manifest) return;
    const products = manifest.products.filter((product) => state.hasMoreByProductId[product.id]);
    if (!products.length) return;

    local.loading = true;
    renderProgressiveReviews();
    const errors = state.errors;
    const fetched = await runWithConcurrency(
      products,
      (product) => fetchProductReviews(manifest, product, (state.productPages[product.id] || 1) + 1),
      maxConcurrent,
      errors
    );
    const nextResults = fetched.map(mapProductPayload);
    nextResults.forEach((next) => {
      const current = state.productReviews[next.productId];
      const byId = new Map([...(current?.reviews || []), ...next.reviews].map((review) => [review.reviewId, review]));
      current.reviews = Array.from(byId.values()).sort(compareReviews);
      current.reviewsReturned = current.reviews.length;
      state.productPages[next.productId] = next.page;
      state.hasMoreByProductId[next.productId] = next.hasMore;
    });
    state.allCandidates = selectAllCandidates(Object.values(state.productReviews));
    state.carouselCandidates = state.allCandidates.slice(0, carouselLimit);
    local.visibleCount += pageSize;
    local.loading = false;
    publishState({ status: errors.length ? "ready_with_errors" : "ready" });
  };

  const init = async () => {
    const token = ++loadToken;
    const manifest = parseManifest();
    if (!manifest || !manifest.products.length) return;

    const errors = [];

    publishState({
      status: "loading",
      collectionHandle: manifest.collectionHandle,
      productIds: manifest.products.map((product) => product.id),
      productReviews: {},
      featuredByProductId: {},
      carouselCandidates: [],
      allCandidates: [],
      productPages: {},
      hasMoreByProductId: {},
      requestCount: 0,
      errors
    });

    const fetched = await runWithConcurrency(
      manifest.products,
      (product) => fetchProductReviews(manifest, product, 1),
      maxConcurrent,
      errors
    );
    if (token !== loadToken || manifestElement !== document.querySelector(manifestSelector)) return;
    const productResults = fetched.map(mapProductPayload);
    const featuredByProductId = productResults.reduce((accumulator, result) => {
      if (result.selectedReview) accumulator[result.productId] = result.selectedReview;
      return accumulator;
    }, {});

    publishState({
      status: errors.length ? "ready_with_errors" : "ready",
      productReviews: productResults.reduce((accumulator, result) => {
        accumulator[result.productId] = result;
        return accumulator;
      }, {}),
      featuredByProductId,
      carouselCandidates: selectCarouselCandidates(productResults),
      allCandidates: selectAllCandidates(productResults),
      productPages: Object.fromEntries(productResults.map((result) => [result.productId, 1])),
      hasMoreByProductId: Object.fromEntries(productResults.map((result) => [result.productId, result.hasMore])),
      errors
    });
  };

  const start = () => {
    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-bb-review-more], [data-bb-home-review-more]");
      if (button) loadMoreReviews(button.closest("[data-bb-progressive-reviews], [data-bb-home-reviews]"));
    });
    const gridContainer = document.getElementById("ProductGridContainer");
    if (gridContainer) {
      new MutationObserver(() => {
        if (document.querySelector(manifestSelector) !== manifestElement) init();
      }).observe(gridContainer, { childList: true, subtree: true });
    }
    mobileInitialCountMedia.addEventListener("change", renderProgressiveReviews);
    init();
  };

  window.BitebackProductGroupReviews = state;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
